import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import FilterDropdown from '../../../../../../Components/FilterDropdown/FilterDropdown';
import { TaskByEmployee } from '../../../../../../Models/Tasks/TaskModels';
import './TasksFilterBar.css';

interface TasksFilterBarProps {
  showMyTasks: boolean;
  onToggleTasks: (showMyTasks: boolean) => void;
  totalTasks: number;
  filteredTasks: number;
  tasks: TaskByEmployee[];
  matterFilter?: string;
  scopeFilter?: string;
  priorityFilter?: string;
  onMatterFilterChange?: (value: string) => void;
  onScopeFilterChange?: (value: string) => void;
  onPriorityFilterChange?: (value: string) => void;
  onSearch?: (searchTerm: string) => void;
  onFilterClick?: () => void;
  onAddTask?: () => void;
}

const TasksFilterBar: React.FC<TasksFilterBarProps> = ({
  showMyTasks,
  onToggleTasks,
  totalTasks,
  filteredTasks,
  tasks,
  matterFilter = 'all',
  scopeFilter = 'all',
  priorityFilter = 'all',
  onMatterFilterChange,
  onScopeFilterChange,
  onPriorityFilterChange,
  onSearch,
  onFilterClick,
  onAddTask,
}) => {
  const [searchTerm, setSearchTerm] = React.useState<string>('');

  // Extract unique values for filter options
  const filterOptions = useMemo(() => {
    const matters = Array.from(new Set(tasks.map((t) => t.mattername).filter(Boolean)));

    return {
      matters: matters.map((m) => ({ value: m || '', label: m || '' })),
      scopes: [
        { value: 'all', label: 'All Scopes' },
        { value: 'internal', label: 'Internal' },
        { value: 'external', label: 'External' },
      ],
      priorities: [
        { value: 'all', label: 'All Priorities' },
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' },
      ],
    };
  }, [tasks]);

  const handleMatterChange = (value: string | string[]) => {
    const val = Array.isArray(value) ? value[0] : value;
    if (onMatterFilterChange) {
      onMatterFilterChange(val);
    }
  };

  const handleScopeChange = (value: string | string[]) => {
    const val = Array.isArray(value) ? value[0] : value;
    if (onScopeFilterChange) {
      onScopeFilterChange(val);
    }
  };

  const handlePriorityChange = (value: string | string[]) => {
    const val = Array.isArray(value) ? value[0] : value;
    if (onPriorityFilterChange) {
      onPriorityFilterChange(val);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(searchTerm);
    }
  };

  return (
    <div className="tasks-filter-bar">
      {/* Top Row: Toggle Tabs and Count */}
      <div className="tasks-filter-bar-top">
        <div className="tasks-filter-tabs">
          <button
            className={`tasks-filter-tab ${showMyTasks ? 'active' : ''}`}
            onClick={() => onToggleTasks(true)}
          >
            My Tasks
          </button>
          <button
            className={`tasks-filter-tab ${!showMyTasks ? 'active' : ''}`}
            onClick={() => onToggleTasks(false)}
          >
            Team's Tasks
          </button>
        </div>
        <div className="tasks-filter-top-right">
          {onAddTask && (
            <button
              type="button"
              className="tasks-filter-add-task"
              onClick={onAddTask}
              aria-label="Add task"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span className="tasks-filter-add-task-text">Add Task</span>
            </button>
          )}
          <div className="tasks-filter-count">
            Showing {filteredTasks} of {totalTasks} tasks
          </div>
        </div>
      </div>

      {/* Bottom Row: Filter Dropdowns, Search, and Filter Button */}
      <div className="tasks-filter-bar-bottom">
        <div className="tasks-filter-dropdowns">
          <FilterDropdown
            label=""
            placeholder="All Projects"
            options={[
              { value: 'all', label: 'All Projects' },
              ...filterOptions.matters,
            ]}
            value={matterFilter}
            onChange={handleMatterChange}
            className="tasks-filter-dropdown"
          />
          <FilterDropdown
            label=""
            placeholder="All Scopes"
            options={filterOptions.scopes}
            value={scopeFilter}
            onChange={handleScopeChange}
            className="tasks-filter-dropdown"
          />
          <FilterDropdown
            label=""
            placeholder="All Priorities"
            options={filterOptions.priorities}
            value={priorityFilter}
            onChange={handlePriorityChange}
            className="tasks-filter-dropdown"
          />
        </div>
        <div className="tasks-filter-search-container">
          <div className="tasks-filter-search-wrapper">
            <FontAwesomeIcon icon={faSearch} className="tasks-filter-search-icon" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              className="tasks-filter-search-input"
            />
          </div>
          <button
            className="tasks-filter-button"
            onClick={onFilterClick}
            aria-label="Filter options"
          >
            <FontAwesomeIcon icon={faFilter} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TasksFilterBar;

